import React from 'react';
import { FieldArrayWithId, UseFormReturn } from 'react-hook-form';
import { UseTrackedFieldArrayRemove } from '../../../../forms/use-tracked-field-array';
import { EventPanelFormType, EventPanelOptions } from '@mark43/cad-api';
import { EventNotificationsEntityIdProvider } from '../../../../event-notifications/event-notifications-entity-id-context';
import { FormGroup, HStack, IconButton, Text, Box, DataList, DataItem } from '@cad-arc';
import { FormInputButton } from '../../../../forms/integrated-form-components/form-button';
import { PropertyBaseKey } from '../types';
import { EventPanelFormPropertyCardControls } from './event-panel-form-property-card-controls';
import { EventPanelFormFirearmPropertyInputs } from './event-panel-form-firearm-properties-inputs';
import { usePropertyFieldsetState } from './use-property-fieldset-state';
import { EventPanelFormDrugsPropertyInputs } from './event-panel-form-drugs-properties-inputs';
import { EventPanelFormOtherItemsPropertyInputs } from './event-panel-form-other-items-properties-inputs';
import { getPropertyNameForControl } from './property-helper';

function _PropertyFieldset(props: {
    form: UseFormReturn<EventPanelFormType>;
    index: number;
    field: FieldArrayWithId<
        EventPanelFormType,
        'firearms' | 'mergedFirearms' | 'drugs' | 'mergedDrugs' | 'otherItems' | 'mergedOtherItems',
        'rhfKey'
    >;
    remove: UseTrackedFieldArrayRemove;
    eventPanelOptions: EventPanelOptions;
    allowMembersControl?: boolean;
    displayAsCards?: boolean;
    baseKey: PropertyBaseKey;
    eventId?: number | null;
}): React.ReactElement {
    const {
        form: eventForm,
        index,
        field,
        eventPanelOptions,
        remove,
        baseKey,
        displayAsCards,
        allowMembersControl,
        eventId,
    } = props;
    const isNewEvent = !eventId;

    const {
        onOpenMergedEvent,
        setShowExtraFields,
        isPersisted,
        showExtraFields,
        showDexFields,
        propertyId,
    } = usePropertyFieldsetState(props);
    return (
        <EventNotificationsEntityIdProvider entityId={propertyId ?? undefined}>
            <>
                {field.eventInfo?.isChildEvent &&
                (baseKey === 'mergedFirearms' ||
                    baseKey === 'mergedDrugs' ||
                    baseKey === 'mergedOtherItems') ? (
                    <DataList variant="stacked">
                        <DataItem dataKey="Merged Event">
                            <HStack>
                                <Text>{field.eventInfo?.agencyEventNumber}</Text>
                                <IconButton
                                    size="sm"
                                    variant="ghost"
                                    icon="OpenNewWindow"
                                    aria-label="open event"
                                    onClick={() => onOpenMergedEvent()}
                                />
                            </HStack>
                        </DataItem>
                    </DataList>
                ) : undefined}

                <EventPanelFormPropertyCardControls
                    eventForm={eventForm}
                    remove={remove}
                    allowMembersControl={allowMembersControl}
                    baseKey={baseKey}
                    index={index}
                    propertyName={getPropertyNameForControl(baseKey)}
                    dexEnabled={showDexFields}
                    isPersisted={isPersisted}
                    isNewEvent={isNewEvent}
                />

                <FormGroup
                    style={{ flex: 1, paddingBottom: 'var(--arc-space-1)' }}
                    data-testid={`property-card-${baseKey}-${index}`}
                >
                    {(baseKey === 'firearms' || baseKey === 'mergedFirearms') && (
                        <EventPanelFormFirearmPropertyInputs
                            form={eventForm}
                            index={index}
                            baseKey={baseKey}
                            eventPanelOptions={eventPanelOptions}
                            showExtraFields={showExtraFields}
                            isPersisted={isPersisted}
                        />
                    )}
                    {(baseKey === 'drugs' || baseKey === 'mergedDrugs') && (
                        <EventPanelFormDrugsPropertyInputs
                            form={eventForm}
                            index={index}
                            baseKey={baseKey}
                            eventPanelOptions={eventPanelOptions}
                            showExtraFields={showExtraFields}
                            isPersisted={isPersisted}
                        />
                    )}
                    {(baseKey === 'otherItems' || baseKey === 'mergedOtherItems') && (
                        <EventPanelFormOtherItemsPropertyInputs
                            form={eventForm}
                            index={index}
                            baseKey={baseKey}
                            eventPanelOptions={eventPanelOptions}
                            showExtraFields={showExtraFields}
                            isPersisted={isPersisted}
                        />
                    )}
                </FormGroup>
                {displayAsCards ? (
                    <Box width="100%" pt={4}>
                        <IconButton
                            w="100%"
                            variant="ghost"
                            onClick={() => setShowExtraFields((last) => !last)}
                            icon={showExtraFields ? 'Close' : 'Open'}
                            data-testid={`${baseKey}-${index}-expand`}
                        />
                    </Box>
                ) : (
                    <FormInputButton
                        size="sm"
                        variant="ghost"
                        leadingVisual={showExtraFields ? 'Close' : 'Open'}
                        onClick={() => setShowExtraFields((last) => !last)}
                    >
                        {showExtraFields ? 'Hide details' : 'Show details'}
                    </FormInputButton>
                )}
            </>
        </EventNotificationsEntityIdProvider>
    );
}

export const PropertyFieldset = React.memo(_PropertyFieldset, (prevProps, nextProps) => {
    return (
        prevProps.eventPanelOptions === nextProps.eventPanelOptions &&
        prevProps.remove === nextProps.remove &&
        prevProps.index === nextProps.index &&
        prevProps.form === nextProps.form
    );
});
